import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Home, User, Building2, Mail, Phone, MapPin, Briefcase, Award,
  Globe, Linkedin, Upload, CheckCircle, Send, Loader2, AlertCircle,
  ChevronDown, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Multi-select options
const solutionTypes = [
  'Strategy / Management Consulting',
  'Digital Transformation / Industry 4.0',
  'AI / Data / Analytics Solutions',
  'Process Improvement / Lean / Six Sigma',
  'People & Capability Development',
  'Technology Implementation / Systems Integrator',
  'Other'
];

const industries = [
  'Oil & Gas / Energy',
  'EPC (Engineering, Procurement & Construction)',
  'Manufacturing',
  'MSMEs',
  'Services',
  'Healthcare',
  'Financial Services',
  'Retail',
  'Technology',
  'Other'
];

const capabilityAreas = [
  'Assessment & Benchmarking',
  'Performance Improvement',
  'Cost Optimization',
  'Revenue Growth / Sales Excellence',
  'Operational Excellence',
  'Supply Chain & Logistics',
  'ESG / Sustainability',
  'Change Management & Adoption'
];

const engagementModels = [
  'Advisory / Consulting',
  'Training / Workshops',
  'Managed Services',
  'Tool / Platform Subscription',
  'One-time Diagnostic / Assessment'
];

const geographies = [
  'India (On-site)',
  'India (Remote)',
  'Global (Remote)',
  'Other'
];

const contactChannels = ['Email', 'WhatsApp', 'Phone Call'];
const experienceRanges = ['0-5 years', '6-10 years', '11-20 years', '20+ years'];
const clientSizes = ['MSME', 'Mid-market', 'Large Enterprise'];

// Multi-select component
const MultiSelect = ({ label, options, selected, onChange, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div
        className="min-h-[48px] p-3 border border-gray-300 rounded-lg cursor-pointer bg-white hover:border-emerald-500 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {selected.length === 0 ? (
              <span className="text-gray-400">Select options...</span>
            ) : (
              selected.map(item => (
                <span key={item} className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-sm flex items-center gap-1">
                  {item}
                  <X className="w-3 h-3 cursor-pointer hover:text-emerald-900" onClick={(e) => { e.stopPropagation(); toggleOption(item); }} />
                </span>
              ))
            )}
          </div>
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.map(option => (
            <div
              key={option}
              className={`px-4 py-2 cursor-pointer hover:bg-emerald-50 flex items-center gap-2 ${selected.includes(option) ? 'bg-emerald-50' : ''}`}
              onClick={(e) => { e.stopPropagation(); toggleOption(option); }}
            >
              <div className={`w-4 h-4 border rounded flex items-center justify-center ${selected.includes(option) ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                {selected.includes(option) && <CheckCircle className="w-3 h-3 text-white" />}
              </div>
              <span className="text-sm">{option}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SolutionProviderPage = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    // Basic Information
    fullName: '',
    professionalTitle: '',
    organisation: '',
    workEmail: '',
    phone: '',
    city: '',
    country: '',
    
    // Professional Profile
    solutionTypes: [],
    otherSolutionType: '',
    industries: [],
    experience: '',
    
    // Offerings and Expertise
    headline: '',
    description: '',
    capabilities: [],
    clientSize: [],
    
    // Evidence of Credibility
    linkedinUrl: '',
    websiteUrl: '',
    caseStudyUrl: '',
    certifications: '',
    
    // Engagement Preferences
    engagementModels: [],
    geographies: [],
    otherGeography: '',
    preferredContact: '',
    
    // Consent
    confirmAccuracy: false,
    agreeContact: false,
    subscribeNewsletter: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.fullName) return 'Please enter your full name';
    if (!formData.professionalTitle) return 'Please enter your professional title';
    if (!formData.organisation) return 'Please enter your organisation name';
    if (!formData.workEmail) return 'Please enter your work email';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) return 'Please enter a valid email';
    if (!formData.phone) return 'Please enter your phone number';
    if (!formData.city || !formData.country) return 'Please enter your city and country';
    if (formData.solutionTypes.length === 0) return 'Please select at least one solution type';
    if (formData.industries.length === 0) return 'Please select at least one industry';
    if (!formData.experience) return 'Please select your years of experience';
    if (!formData.headline) return 'Please enter a headline for your offering';
    if (!formData.description) return 'Please describe your services';
    if (formData.capabilities.length === 0) return 'Please select at least one capability area';
    if (formData.clientSize.length === 0) return 'Please select typical client size';
    if (formData.engagementModels.length === 0) return 'Please select at least one engagement model';
    if (formData.geographies.length === 0) return 'Please select at least one geography';
    if (!formData.preferredContact) return 'Please select preferred contact channel';
    if (!formData.confirmAccuracy) return 'Please confirm the accuracy of your information';
    if (!formData.agreeContact) return 'Please agree to be contacted by SĀMANVAI';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/solution-provider/register`, formData);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Registration Submitted Successfully!</h1>
          <p className="text-gray-600 mb-8">
            Thank you for registering as a Solution Provider on SĀMANVAI. Our team will review your application 
            and get back to you within 2-3 business days.
          </p>
          <Link to="/">
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <Link to="/">
          <Button variant="outline" className="gap-2 text-gray-600 hover:text-emerald-600 hover:border-emerald-600 mb-6">
            <Home className="w-4 h-4" />
            Home
          </Button>
        </Link>
        
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Become a Solution Provider
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join SĀMANVAI&apos;s network of consultants, coaches, technology vendors, and specialist agencies 
            to connect with organizations seeking transformation solutions.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-8" data-testid="solution-provider-form">
        {/* Section 1: Basic Information */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-emerald-600" />
            Basic Information
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <Input
                data-testid="input-fullname"
                value={formData.fullName}
                onChange={(e) => handleChange('fullName', e.target.value)}
                placeholder="John Doe"
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Professional Title / Role *</label>
              <Input
                value={formData.professionalTitle}
                onChange={(e) => handleChange('professionalTitle', e.target.value)}
                placeholder="Senior Consultant"
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Organisation / Firm Name *</label>
              <Input
                value={formData.organisation}
                onChange={(e) => handleChange('organisation', e.target.value)}
                placeholder="ABC Consulting"
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Work Email *</label>
              <Input
                data-testid="input-email"
                type="email"
                value={formData.workEmail}
                onChange={(e) => handleChange('workEmail', e.target.value)}
                placeholder="john@company.com"
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mobile / WhatsApp Number *</label>
              <Input
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+91 98765 43210"
                className="h-12"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Mumbai"
                  className="h-12"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                <Input
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  placeholder="India"
                  className="h-12"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Professional Profile */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-emerald-600" />
            Professional Profile
          </h2>
          
          <div className="space-y-5">
            <MultiSelect
              label="Type of Solution Provider *"
              options={solutionTypes}
              selected={formData.solutionTypes}
              onChange={(v) => handleChange('solutionTypes', v)}
            />
            
            {formData.solutionTypes.includes('Other') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Please specify other solution type</label>
                <Input
                  value={formData.otherSolutionType}
                  onChange={(e) => handleChange('otherSolutionType', e.target.value)}
                  placeholder="Describe your solution type"
                  className="h-12"
                />
              </div>
            )}
            
            <MultiSelect
              label="Primary Industries Served *"
              options={industries}
              selected={formData.industries}
              onChange={(v) => handleChange('industries', v)}
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience *</label>
              <Select value={formData.experience} onValueChange={(v) => handleChange('experience', v)}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Select experience range" />
                </SelectTrigger>
                <SelectContent>
                  {experienceRanges.map(range => (
                    <SelectItem key={range} value={range}>{range}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Section 3: Offerings and Expertise */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Award className="w-5 h-5 text-emerald-600" />
            Offerings and Expertise
          </h2>
          
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Headline Describing Your Offering *</label>
              <Input
                value={formData.headline}
                onChange={(e) => handleChange('headline', e.target.value)}
                placeholder="e.g., AI-driven maintenance optimization for process plants"
                className="h-12"
                maxLength={100}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.headline.length}/100 characters</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Brief Description of Services *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe your services in 3-4 lines..."
                className="min-h-[120px]"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
            </div>
            
            <MultiSelect
              label="Top Capability Areas (Select up to 3) *"
              options={capabilityAreas}
              selected={formData.capabilities}
              onChange={(v) => handleChange('capabilities', v.slice(0, 3))}
            />
            
            <MultiSelect
              label="Typical Client Size *"
              options={clientSizes}
              selected={formData.clientSize}
              onChange={(v) => handleChange('clientSize', v)}
            />
          </div>
        </div>

        {/* Section 4: Evidence of Credibility */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-600" />
            Evidence of Credibility
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Linkedin className="w-4 h-4 inline mr-1" />
                LinkedIn Profile URL
              </label>
              <Input
                value={formData.linkedinUrl}
                onChange={(e) => handleChange('linkedinUrl', e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Website / Portfolio URL</label>
              <Input
                value={formData.websiteUrl}
                onChange={(e) => handleChange('websiteUrl', e.target.value)}
                placeholder="https://yourcompany.com"
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Case Study / Success Story URL</label>
              <Input
                value={formData.caseStudyUrl}
                onChange={(e) => handleChange('caseStudyUrl', e.target.value)}
                placeholder="Link to case study or portfolio"
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Certifications</label>
              <Input
                value={formData.certifications}
                onChange={(e) => handleChange('certifications', e.target.value)}
                placeholder="e.g., PMP, Six Sigma Black Belt, AWS Certified"
                className="h-12"
              />
            </div>
          </div>
        </div>

        {/* Section 5: Engagement Preferences */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-emerald-600" />
            Engagement Preferences
          </h2>
          
          <div className="space-y-5">
            <MultiSelect
              label="Preferred Engagement Model *"
              options={engagementModels}
              selected={formData.engagementModels}
              onChange={(v) => handleChange('engagementModels', v)}
            />
            
            <MultiSelect
              label="Geographies You Can Support *"
              options={geographies}
              selected={formData.geographies}
              onChange={(v) => handleChange('geographies', v)}
            />
            
            {formData.geographies.includes('Other') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Please specify other geographies</label>
                <Input
                  value={formData.otherGeography}
                  onChange={(e) => handleChange('otherGeography', e.target.value)}
                  placeholder="e.g., Middle East, Southeast Asia"
                  className="h-12"
                />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Channel *</label>
              <Select value={formData.preferredContact} onValueChange={(v) => handleChange('preferredContact', v)}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Select preferred channel" />
                </SelectTrigger>
                <SelectContent>
                  {contactChannels.map(channel => (
                    <SelectItem key={channel} value={channel}>{channel}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Section 6: Consent and Declarations */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            Consent and Declarations
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="confirmAccuracy"
                checked={formData.confirmAccuracy}
                onCheckedChange={(checked) => handleChange('confirmAccuracy', checked)}
                className="mt-1"
              />
              <label htmlFor="confirmAccuracy" className="text-sm text-gray-700 cursor-pointer">
                I confirm that the information provided is accurate to the best of my knowledge. *
              </label>
            </div>
            
            <div className="flex items-start gap-3">
              <Checkbox
                id="agreeContact"
                checked={formData.agreeContact}
                onCheckedChange={(checked) => handleChange('agreeContact', checked)}
                className="mt-1"
              />
              <label htmlFor="agreeContact" className="text-sm text-gray-700 cursor-pointer">
                I agree to be contacted by SĀMANVAI regarding listing and potential client opportunities. *
              </label>
            </div>
            
            <div className="flex items-start gap-3">
              <Checkbox
                id="subscribeNewsletter"
                checked={formData.subscribeNewsletter}
                onCheckedChange={(checked) => handleChange('subscribeNewsletter', checked)}
                className="mt-1"
              />
              <label htmlFor="subscribeNewsletter" className="text-sm text-gray-700 cursor-pointer">
                I would like to receive updates, newsletters, and insights from SĀMANVAI.
              </label>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Submit Button */}
        <div className="text-center">
          <Button
            type="submit"
            disabled={loading}
            data-testid="submit-registration-btn"
            className="bg-emerald-600 hover:bg-emerald-700 px-12 py-6 text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit Registration
              </>
            )}
          </Button>
          <p className="text-sm text-gray-500 mt-4">
            By submitting, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </form>
    </div>
  );
};

export default SolutionProviderPage;
